import { createSlice } from '@reduxjs/toolkit';
import { callApi } from 'src/utils/apiCall';
import { updateError } from './user';

const INITIAL_STATE = {
    leadOverview: [],
    practitioners: [],
    revenueDistribution: [],
    revenueDistributionByTreatment: [],
    leadSources: [],
    referrals: [],
    statusMatrix: [],
    treatmentEnquiries: [],
    _allPracticesFetchKey: null,
};

const STATUS_SET = {
    'NEW': "NEW",
    'CONSULTATION_BOOKED': "CONSULTATION_BOOKED",
    'CONSULTATION_ATTENDED': "CONSULTATION_ATTENDED",
    'TREATMENT_STARTED': "TREATMENT_STARTED",
    'CLOSED': "CLOSED"
};

const slice = createSlice({
    name: 'allPracticeReports',
    initialState: INITIAL_STATE,
    reducers: {
        setLeadOverviewTable: (state, action) => { state.leadOverview = action.payload; },
        setPractitionerSummary: (state, action) => { state.practitioners = action.payload; },
        setRevenueByCategory: (state, action) => { state.revenueDistribution = action.payload; },
        setRevenueByTreatmentName: (state, action) => { state.revenueDistributionByTreatment = action.payload; },
        setLeadSourceDist: (state, action) => { state.leadSources = action.payload; },
        setReferralDist: (state, action) => { state.referrals = action.payload; },
        setTreatmentStatusMatrix: (state, action) => { state.statusMatrix = action.payload; },
        setTreatmentEnquiryCount: (state, action) => { state.treatmentEnquiries = action.payload; },
        setAllPracticesFetchKey: (state, action) => { state._allPracticesFetchKey = action.payload; },
    },
});

export const {
    setLeadOverviewTable,
    setPractitionerSummary,
    setRevenueByCategory,
    setLeadSourceDist,
    setReferralDist,
    setTreatmentStatusMatrix,
    setTreatmentEnquiryCount,
    setRevenueByTreatmentName,
    setAllPracticesFetchKey,
} = slice.actions;

export function fetchPracticeAnalytics(practiceIds, dateRange) {
    return async (dispatch) => {
        const baseUrl = '/api/v1/practices';

        try {
            const allPracticeData = await Promise.all(
                practiceIds.map(async (practiceId) => {
                    const prefix = `${baseUrl}/${practiceId}/reports`;

                    try {

                        const totalLeadsApi = callApi(`${prefix}/leads/?aggregate[Count]=id&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}`);

                        const totalTreatmentsApi = callApi(`${prefix}/leads/?aggregate[Count]=lead_treatments&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}`);

                        const appointmentStatsApi = callApi(`${prefix}/appointments/?&is_archived=false&group_by[status]=true&aggregate[Count]=id&datetime__gte=${dateRange.start}&datetime__lte=${dateRange.end}`);

                        const treatmentDetailsApi = callApi(`${baseUrl}/${practiceId}/treatments/?enabled=true&ordering=treatment_item__name&fields={id,practice,price,colour,icon,treatment_item{id,name,treatment_category{id,name}}}`);

                        const treatmentRevenueDistApi = callApi(`${prefix}/leads/?aggregate[Count]=id&aggregate[Sum]=lead_treatments__custom_price&group_by[lead_treatments__treatment__treatment_item__treatment_category]=true&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}`);

                        const treatmentRevenueByNameDistApi = callApi(`${prefix}/leads/?aggregate[Count]=id&aggregate[Sum]=lead_treatments__custom_price&group_by[lead_treatments__treatment]=true&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}`);

                        const treatmentStateStatsApi = callApi(`${prefix}/leads/?&aggregate[Count]=id&aggregate[Sum]=lead_treatments__custom_price&group_by[id]=true&group_by[lead_treatments__treatment]=true&group_by[lead_treatments__state_transition_logs__state]=true&state_transition_params=${encodeURIComponent(JSON.stringify({ timestamp_gte: dateRange.start, timestamp_lte: dateRange.end }))}`);

                        const practitionerStatsApi = callApi(`${prefix}/practice-user/`);

                        const leadSourceStatsApi = callApi(`${prefix}/leads/?group_by[lead_source__type]=true&aggregate[Count]=id&aggregate[Sum]=lead_treatments__custom_price&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}`);

                        const leadReferralStatsApi = callApi(`${prefix}/leads/?group_by[lead_source__referral]=true&aggregate[Count]=id&aggregate[Sum]=lead_treatments__custom_price&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}`);

                        const treatmentEnquiryCountsApi = callApi(`${prefix}/leads/?aggregate[Count]=id&group_by[lead_treatments__treatment__treatment_item__treatment_category]=true&lead_treatments__status=NEW&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}`);

                        const archivedLeadCountsApi = callApi(`${prefix}/leads/?group_by[is_archived]=true&aggregate[Count]=id&aggregate[Sum]=lead_treatments__custom_price&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}`);

                        const [
                            totalLeads,                          // (a) Total leads
                            totalTreatments,                     // (a) Total treatments
                            appointmentStats,                    // (a) Appointments booked/attended
                            treatmentDetails,                    // Metadata for treatment mapping
                            treatmentRevenueDist,                // (b, e) Revenue by treatment category
                            treatmentStateStats,                 // (c) Category × Status × Count/Revenue
                            practitionerStats,                   // (d) Conversions by practitioners
                            leadSourceStats,                     // (f) Lead by source
                            leadReferralStats,                   // (g) Lead by referral
                            treatmentEnquiryCounts,               // (h) Treatment vs enquiry count
                            archivedLeadCounts,
                            treatmentRevenueByNameDist
                        ] = await Promise.all([totalLeadsApi,
                            totalTreatmentsApi,
                            appointmentStatsApi,
                            treatmentDetailsApi,
                            treatmentRevenueDistApi,
                            treatmentStateStatsApi,
                            practitionerStatsApi,
                            leadSourceStatsApi,
                            leadReferralStatsApi,
                            treatmentEnquiryCountsApi,
                            archivedLeadCountsApi,
                            treatmentRevenueByNameDistApi]);

                        return {
                            practiceId,
                            totalLeads: totalLeads?.data?.data?.count_id,
                            totalTreatments: totalTreatments?.data?.data?.count_lead_treatments,
                            appointmentStats: appointmentStats?.data?.data || [],
                            treatmentDetails: treatmentDetails?.data?.results || [],
                            treatmentRevenueDist: treatmentRevenueDist?.data?.data || [],
                            treatmentRevenueByNameDist: treatmentRevenueByNameDist?.data?.data || [],
                            treatmentStateStats: treatmentStateStats?.data?.data || [],
                            practitionerStats: practitionerStats?.data || [],
                            leadSourceStats: leadSourceStats?.data?.data || [],
                            leadReferralStats: leadReferralStats?.data?.data || [],
                            treatmentEnquiryCounts: treatmentEnquiryCounts?.data?.data || [],
                            archivedLeadCounts: archivedLeadCounts?.data?.data || []
                        };
                    } catch (err) {
                        console.error(`Error in practice ${practiceId}`, err);
                        return null;
                    }
                })
            );

            const validPracticeData = allPracticeData.filter(Boolean);

            // (a) Total Leads Summary Table
            const leadOverviewTable = validPracticeData.map((p) => ({
                practiceId: p.practiceId,
                totalLeads: p.totalLeads,
                totalTreatments: p.totalTreatments,
                bookedAppointments: ['BOOKED', 'ATTENDED'].reduce((sum, status) => {
                    const match = p.appointmentStats.find((x) => x.status === status);
                    return sum + (match?.count_id || 0);
                }, 0),
                attendedConsultations: p.appointmentStats.find((x) => x.status === 'ATTENDED')?.count_id || 0,
                startedTreatments: (p.treatmentStateStats.filter((x) => x.lead_treatments__state_transition_logs__state === 'TREATMENT_STARTED') ?? [])?.reduce?.((sum, obj) => sum + (obj?.count_id ?? 0), 0) || 0,
                notInterested: p.archivedLeadCounts.find((x) => x.is_archived)?.count_id || 0
            }));
            dispatch(setLeadOverviewTable(leadOverviewTable));

            // (b + e) Revenue by Treatment Category (Named) with color mapping
            let treatmentIdToName = {};
            let treatmentIdToColor = {};
            const revenueByCategory = validPracticeData.map((p) => {
                p.treatmentDetails.forEach((t) => {
                    const id = t.treatment_item?.treatment_category?.id;
                    const name = t.treatment_item?.treatment_category?.name;
                    const color = t.colour;
                    if (id && name) {
                        treatmentIdToName[id] = name;
                        if (color) {
                            treatmentIdToColor[id] = color;
                        }
                    }
                });

                const distribution = p.treatmentRevenueDist
                    .map((cat) => {
                        const categoryId = cat.lead_treatments__treatment__treatment_item__treatment_category;
                        const categoryName = treatmentIdToName[categoryId];
                        if (!categoryName) return null;
                        return {
                            categoryId,
                            categoryName,
                            revenue: cat.sum_lead_treatments__custom_price,
                            color: treatmentIdToColor[categoryId]
                        };
                    })
                    .filter(Boolean);

                return {
                    practiceId: p.practiceId,
                    distribution,
                    totalRevenue: distribution.reduce((acc, d) => acc + d.revenue, 0)
                };
            });
            dispatch(setRevenueByCategory(revenueByCategory));

            treatmentIdToName = {};
            treatmentIdToColor = {};
            const treatmentIdToMeta = {};
            const revenueByTreatmentName = validPracticeData.map((p) => {
                p.treatmentDetails.forEach((t) => {
                    const id = t?.id;
                    const name = t.treatment_item?.name;
                    const color = t.colour;
                    if (id && name) {
                        treatmentIdToName[id] = name;
                        treatmentIdToMeta[id] = { name, color };
                        if (color) {
                            treatmentIdToColor[id] = color;
                        }
                    }
                });

                const distribution = p.treatmentRevenueByNameDist
                    .map((cat) => {
                        const categoryId = cat.lead_treatments__treatment;
                        const categoryName = treatmentIdToName[categoryId];
                        if (!categoryName) return null;
                        return {
                            categoryId,
                            categoryName,
                            revenue: cat.sum_lead_treatments__custom_price,
                            color: treatmentIdToColor[categoryId] || "#FFF5CC"
                        };
                    })
                    .filter(Boolean);

                return {
                    practiceId: p.practiceId,
                    distribution,
                    totalRevenue: distribution.reduce((acc, d) => acc + d.revenue, 0)
                };
            })

            dispatch(setRevenueByTreatmentName(revenueByTreatmentName));

            // (d) Practitioner Conversions Summary
            const practitionerSummary = validPracticeData.flatMap((p) =>
                p.practitionerStats.map((pract) => ({
                    practitioner: `${pract.user.first_name} ${pract.user.last_name}`,
                    practiceId: p.practiceId,
                    treatments: pract.conversions_count,
                    revenue: pract.conversions_value || 0
                }))
            );
            dispatch(setPractitionerSummary(practitionerSummary));

            // (f) Lead Source Distribution
            const leadSourceDist = validPracticeData.map((p) => ({
                practiceId: p.practiceId,
                sources: p.leadSourceStats
            }));
            dispatch(setLeadSourceDist(leadSourceDist));

            // (g) Lead Referral Distribution
            const referralDist = validPracticeData.map((p) => ({
                practiceId: p.practiceId,
                referrals: p.leadReferralStats
            }));
            dispatch(setReferralDist(referralDist));

            // (c) Treatment Category × Status Matrix with categoryName and color
            const treatmentStatusMatrix = validPracticeData.flatMap((p) => p.treatmentStateStats.map((entry) => {
                const categoryId = entry?.lead_treatments__treatment;
                const meta = treatmentIdToMeta[categoryId] || {};
                return {
                    practiceId: p.practiceId,
                    categoryId,
                    categoryName: meta.name || 'Unknown Treatment',
                    id: entry?.id,
                    color: meta.color,
                    status: entry.lead_treatments__state_transition_logs__state,
                    count: entry.count_id,
                    revenue: entry.sum_lead_treatments__custom_price
                }
            }));

            const groupedMap = new Map();

            treatmentStatusMatrix?.forEach(item => {
                const key = `${item.practiceId}__${item.categoryName}__${STATUS_SET[item.status] ?? "OTHERS"}`;

                if (!groupedMap.has(key)) {
                    groupedMap.set(key, {
                        practiceId: item.practiceId,
                        categoryName: item.categoryName,
                        categoryId: item.categoryId,
                        color: item.color,
                        status: STATUS_SET[item.status] ?? "OTHERS",
                        count: 0,
                        revenue: 0,
                        ids: []
                    });
                }

                const group = groupedMap.get(key);

                group.count += item.count;
                group.revenue += item.revenue;
                group.ids.push(item.id);

                if (!group.color && item?.color) {
                    group.color = item.color;
                }
                if (!group.categoryId && item?.categoryId) {
                    group.categoryId = item.categoryId;
                }
            });

            const result = Array.from(groupedMap.values());

            dispatch(setTreatmentStatusMatrix(result));

            // (h) Enquiry Count by Treatment Category (NEW status only)
            const treatmentEnquiryCount = validPracticeData.map((p) => ({
                practiceId: p.practiceId,
                enquiries: p.treatmentEnquiryCounts.map((item) => ({
                    categoryId: item.lead_treatments__treatment__treatment_item__treatment_category,
                    count: item.count_id
                }))
            }));
            dispatch(setTreatmentEnquiryCount(treatmentEnquiryCount));

            dispatch(setAllPracticesFetchKey(`${practiceIds.sort().join(',')}|${dateRange.start}|${dateRange.end}`));
            dispatch(updateError({ message: 'Analytics loaded successfully', success: true }));
        } catch (error) {
            console.error('Failed to fetch analytics', error);
            dispatch(updateError({ message: error.message || 'Analytics load failed', success: false }));
        }
    };
}

// Reducer
export default slice.reducer;